import React from "react";
import {
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    Grid,
    InputLabel,
    OutlinedInput,
    Typography,
    Chip,
    Select,
    MenuItem
} from "@mui/material";
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import DefaultDrawer from "../../../components/default-drawer";
import { useMenusController } from "../context/menus-controller";
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent
} from '@dnd-kit/core';
import {
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { MenuItemForm } from "./menu-item-form";

export const MenuDrawer = () => {
    const {
        showAddEdit,
        dataRequest,
        dataResponse,
        save,
        closeForm,
        handleOnChangeForm,
        addNewItem,
        removeItemByIndex,
        updateItem,
        reorderItems,
        customPagesList
    } = useMenusController();

    const [expandedItemIndex, setExpandedItemIndex] = React.useState<number | null>(null);

    const isHomeMenu = dataResponse?.slug?.toLowerCase() === 'home';

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (over && active.id !== over.id) {
            const oldIndex = dataRequest.items.findIndex((_, idx) => `item-${idx}` === active.id);
            const newIndex = dataRequest.items.findIndex((_, idx) => `item-${idx}` === over.id);

            if (oldIndex !== -1 && newIndex !== -1) {
                reorderItems(oldIndex, newIndex);
            }
        }
    };

    const actions = () => {
        return (
            <React.Fragment>
                <Grid item xs={12} sx={{ mt: 3 }}>
                    <Box
                        display="flex"
                        justifyContent="flex-end"
                        sx={{
                            borderTop: '1px solid #e0e0e0',
                            pt: 2
                        }}
                    >
                        <Button
                            variant="contained"
                            onClick={save}
                            startIcon={<SaveIcon />}
                            color="primary"
                            disabled={isHomeMenu}
                        >
                            Salvar
                        </Button>
                    </Box>
                </Grid>
            </React.Fragment>
        );
    };

    return (
        <DefaultDrawer
            isOpen={showAddEdit}
            title={
                <Box display="flex" alignItems="center" gap={1}>
                    {dataRequest?.id ? 'Editar Menu' : 'Novo Menu'}
                    {isHomeMenu && (
                        <Chip label="HOME - Somente Leitura" size="small" color="error" />
                    )}
                    {!isHomeMenu && dataResponse?.isDefault && (
                        <Chip label="Menu Padrão" size="small" color="primary" />
                    )}
                </Box>
            }
            onClose={closeForm}
            actions={() => actions()}
        >
            <Box>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                    {/* Título e Visível */}
                    <Grid item xs={12} md={10}>
                        <InputLabel>Título *</InputLabel>
                        <OutlinedInput
                            size="small"
                            value={dataRequest?.title || ''}
                            onChange={(e) => handleOnChangeForm('title', e.target.value)}
                            fullWidth
                            error={!dataRequest?.title || dataRequest.title.length === 0}
                            placeholder="Digite o título do menu..."
                            disabled={isHomeMenu || dataResponse?.isDefault}
                        />
                    </Grid>
                    <Grid item xs={12} md={2} display="flex" alignItems="flex-end">
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={dataRequest?.visible || false}
                                    onChange={(e) => handleOnChangeForm('visible', e.target.checked)}
                                    color="primary"
                                    disabled={isHomeMenu}
                                />
                            }
                            label="Visível"
                        />
                    </Grid>

                    {/* Divider e Vinculação do Menu - oculto para menus padrão */}
                    {!dataResponse?.isDefault && (
                        <>
                            <Grid item xs={12}>
                                <Box sx={{ borderBottom: '2px solid #ddd', my: 2 }} />
                                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                                    Vinculação do Menu
                                </Typography>
                                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                                    O menu pode apontar para uma página customizada ou URL externa (opcional)
                                </Typography>
                            </Grid>

                            {/* Página Customizada */}
                            <Grid item xs={12}>
                                <InputLabel>Página Customizada</InputLabel>
                                <Select
                                    size="small"
                                    value={dataRequest?.customPageId || ''}
                                    onChange={(e) => handleOnChangeForm('customPageId', e.target.value || null)}
                                    fullWidth
                                    disabled={isHomeMenu}
                                >
                                    <MenuItem value="">Nenhuma (usar URL externa ou sem vinculação)</MenuItem>
                                    {customPagesList.map(page => (
                                        <MenuItem key={page.id} value={page.id}>
                                            {page.title}
                                        </MenuItem>
                                    ))}
                                </Select>
                                <Typography variant="caption" color="textSecondary">
                                    Selecione uma página customizada ou deixe em branco
                                </Typography>
                            </Grid>

                            {/* URL Externa (se não tem customPageId) */}
                            {!dataRequest?.customPageId && (
                                <Grid item xs={12}>
                                    <InputLabel>URL Externa</InputLabel>
                                    <OutlinedInput
                                        size="small"
                                        value={dataRequest?.externalUrl || ''}
                                        onChange={(e) => handleOnChangeForm('externalUrl', e.target.value)}
                                        placeholder="https://..."
                                        fullWidth
                                        disabled={isHomeMenu}
                                    />
                                    <Typography variant="caption" color="textSecondary">
                                        Deixe em branco se não quiser vincular o menu a nenhuma URL
                                    </Typography>
                                </Grid>
                            )}
                        </>
                    )}

                    {/* Divider */}
                    <Grid item xs={12}>
                        <Box sx={{ borderBottom: '2px solid #ddd', my: 2 }} />
                    </Grid>

                    {/* Items Header */}
                    <Grid item xs={12}>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="h6" fontWeight="bold">
                                Itens do Menu
                            </Typography>
                            <Button
                                variant="contained"
                                size="small"
                                startIcon={<AddIcon />}
                                onClick={() => {
                                    addNewItem();
                                    // Expandir o último item (recém-adicionado)
                                    setExpandedItemIndex(dataRequest.items.length);
                                }}
                                disabled={isHomeMenu}
                            >
                                Adicionar Item
                            </Button>
                        </Box>
                        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                            {isHomeMenu
                                ? 'Menu HOME não pode ser editado. Os itens são somente leitura.'
                                : dataResponse?.isDefault
                                ? 'Menu padrão: você pode adicionar novos itens, mas itens padrão existentes não podem ser editados ou excluídos.'
                                : 'Adicione pelo menos um item ao menu. Cada item pode apontar para uma página customizada ou URL externa.'}
                        </Typography>
                    </Grid>

                    {/* Items List */}
                    <Grid item xs={12}>
                        {dataRequest?.items && dataRequest.items.length > 0 ? (
                            <DndContext
                                sensors={sensors}
                                collisionDetection={closestCenter}
                                onDragEnd={handleDragEnd}
                            >
                                <SortableContext
                                    items={dataRequest.items.map((_, idx) => `item-${idx}`)}
                                    strategy={verticalListSortingStrategy}
                                >
                                    {dataRequest.items.map((item, index) => (
                                        <MenuItemForm
                                            key={`item-${index}`}
                                            id={`item-${index}`}
                                            item={item}
                                            itemIndex={index}
                                            onUpdate={updateItem}
                                            onRemove={removeItemByIndex}
                                            customPagesList={customPagesList}
                                            disabled={isHomeMenu}
                                            expanded={expandedItemIndex === index}
                                            onExpandChange={(itemIndex, isExpanded) => {
                                                setExpandedItemIndex(isExpanded ? itemIndex : null);
                                            }}
                                        />
                                    ))}
                                </SortableContext>
                            </DndContext>
                        ) : (
                            <Box
                                sx={{
                                    p: 4,
                                    textAlign: 'center',
                                    backgroundColor: '#f5f5f5',
                                    borderRadius: 2,
                                    border: '2px dashed #ddd'
                                }}
                            >
                                <Typography variant="body1" color="textSecondary">
                                    Nenhum item adicionado ainda
                                </Typography>
                                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                                    Clique em "Adicionar Item" para começar a criar os itens do menu
                                </Typography>
                            </Box>
                        )}
                    </Grid>
                </Grid>
            </Box>
        </DefaultDrawer>
    );
};
